import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import MediaCard from "../components/MediaCard";
import StoryCard from "../components/StoryCard";
import { useFeedCache, readFeedCache, restoreScroll } from "../hooks/useFeedCache";
import { Sparkles, Search, Loader2, Wand2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  "beefy leather daddy dominating in a dark dungeon",
  "smooth twink in a jockstrap in the locker room",
  "rubber pup on a leash at a fetish party",
  "hairy bear in a harness outdoors",
];

export default function AiSearch() {
  const cached = readFeedCache("aisearch");
  const [query, setQuery] = useState(cached?.query || "");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(cached?.items || []);
  const [stories, setStories] = useState(cached?.stories || []);
  const [interpreted, setInterpreted] = useState(cached?.interpreted || null);
  const [searched, setSearched] = useState(!!cached?.searched);

  useEffect(() => {
    if (cached?.items || cached?.stories) restoreScroll(cached.scrollY);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useFeedCache("aisearch", items, true, { stories, interpreted, query, searched });

  const run = async (preset) => {
    const q = (preset ?? query).trim();
    if (!q) { toast.error("Describe what you're in the mood for"); return; }
    if (preset) setQuery(preset);
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.post("/ksi/search", { query: q });
      setItems(data.items || []);
      setStories(data.stories || []);
      setInterpreted(data.interpreted || null);
      if (!data.items?.length && !data.stories?.length) toast.info("No close matches yet — try different wording or import more media.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Search failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-20 pb-16" data-testid="ai-search-page">
      <div className="flex items-center gap-3 mb-2">
        <Wand2 className="text-[#00E5FF]" size={30} />
        <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter text-white">AI <span className="text-[#00E5FF]">Discover</span></h1>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-6 max-w-2xl">Describe the scene, gear or vibe you're after.</p>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00E5FF]" size={18} />
          <input
            data-testid="ai-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. muscular leather daddy dominating in a dungeon…"
            className="w-full bg-[#141414] border border-[#1C1C1F] text-white pl-10 pr-4 py-3.5 focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#52525B] rounded-xl text-sm"
          />
        </div>
        <button data-testid="ai-search-btn" disabled={loading} className="bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-xs px-6 hover:bg-[#00c9e0] transition-colors disabled:opacity-50 flex items-center gap-2 rounded-xl">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} Search
        </button>
      </form>

      {!searched && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="ai-search-examples">
          {EXAMPLES.map((e) => (
            <button key={e} onClick={() => run(e)} className="text-[#A1A1AA] hover:text-white border border-[#1C1C1F] hover:border-[#00E5FF] rounded-full px-3 py-1.5 text-xs transition-colors">{e}</button>
          ))}
        </div>
      )}

      {interpreted && (interpreted.tags?.length || interpreted.category) && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="ai-search-interpreted">
          <span className="text-[#52525B] font-mono text-[10px] uppercase tracking-wider">Reading as:</span>
          {interpreted.category && <span className="bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider rounded-full">{interpreted.category}</span>}
          {(interpreted.tags || []).map((t) => (
            <span key={t} className="bg-[#141414] text-[#A1A1AA] border border-[#1C1C1F] px-2.5 py-1 font-mono text-[10px] rounded-full">#{t}</span>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-24 text-[#52525B]" data-testid="ai-search-loading">
          <Loader2 className="animate-spin text-[#00E5FF] mb-3" size={32} />
          <p className="font-mono text-xs uppercase tracking-widest">Reading your mind…</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-2 sm:gap-3" data-testid="ai-search-grid">
          {items.map((item, i) => <MediaCard key={item.id} item={item} index={i} />)}
        </div>
      )}

      {!loading && stories.length > 0 && (
        <div className="mt-10" data-testid="ai-search-stories">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={20} className="text-[#00E5FF]" />
            <h2 className="text-white font-heading uppercase tracking-tight text-2xl">Matching stories</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stories.map((s, i) => <StoryCard key={s.id} story={s} index={i} />)}
          </div>
        </div>
      )}

      {!loading && searched && items.length === 0 && stories.length === 0 && (
        <div className="border border-dashed border-[#1C1C1F] py-20 text-center" data-testid="ai-search-empty">
          <Wand2 className="text-[#1C1C1F] mx-auto mb-3" size={40} />
          <p className="text-[#52525B] font-mono text-xs uppercase tracking-widest">No matches — try different words</p>
        </div>
      )}
    </div>
  );
}
